import { watch } from 'vue';
import * as yup from 'yup';
import i18n from './i18n';

// Initialize yup locale immediately when this module is imported
// This ensures the locale is set before any schemas are created
function initializeYupLocale() {
  const { t } = i18n.global;

  // Helper: map field keys to translated labels, fallback to raw path if not translated
  const fieldLabel = (path) => t(`fields.${path}`, { field: path });

  yup.setLocale({
    // 🌐 Rules that apply to ALL types
    /* 
        `required` ->  1. ensures the value is not undefined, not null, and not an empty string (for strings)
                       2. for form inputs (e.g. Name, Email, Password).   
        `defined`  ->  1. ensures the value is not undefined, but allows null and empty string (ensure the field exists, but don’t care if it’s empty)
                       2. for object properties that must exist, even if they can be empty
        */
    mixed: {
      required: ({ path }) => t('validation.required', { field: fieldLabel(path) }),
      defined: ({ path }) => t('validation.defined', { field: fieldLabel(path) }),
      oneOf: ({ values, path }) => t('validation.oneOf', { field: fieldLabel(path), values }),
      notOneOf: ({ values, path }) => t('validation.notOneOf', { field: fieldLabel(path), values }),
      notNull: ({ path }) => t('validation.notNull', { field: fieldLabel(path) }),
    },

    // 🌐 String-specific rules
    string: {
      min: ({ min, path }) => t('validation.min', { field: fieldLabel(path), min }),
      max: ({ max, path }) => t('validation.max', { field: fieldLabel(path), max }),
      length: ({ length, path }) => t('validation.length', { field: fieldLabel(path), length }),
      email: ({ path }) => t('validation.email', { field: fieldLabel(path) }),
      url: ({ path }) => t('validation.url', { field: fieldLabel(path) }),
      uuid: ({ path }) => t('validation.uuid', { field: fieldLabel(path) }),
      matches: ({ regex, path }) => t('validation.matches', { field: fieldLabel(path), regex }),
    },

    // 🌐 Number-specific rules
    number: {
      min: ({ min, path }) => t('validation.minNumber', { field: fieldLabel(path), min }),
      max: ({ max, path }) => t('validation.maxNumber', { field: fieldLabel(path), max }),
      lessThan: ({ less, path }) => t('validation.lessThan', { field: fieldLabel(path), less }),
      moreThan: ({ more, path }) => t('validation.moreThan', { field: fieldLabel(path), more }),
      positive: ({ path }) => t('validation.positive', { field: fieldLabel(path) }),
      negative: ({ path }) => t('validation.negative', { field: fieldLabel(path) }),
      integer: ({ path }) => t('validation.integer', { field: fieldLabel(path) }),
    },

    // 🌐 Date-specific rules
    date: {
      min: ({ min, path }) => t('validation.minDate', { field: fieldLabel(path), min }),
      max: ({ max, path }) => t('validation.maxDate', { field: fieldLabel(path), max }),
    },

    // 🌐 Array-specific rules
    array: {
      min: ({ min, path }) => t('validation.minArray', { field: fieldLabel(path), min }),
      max: ({ max, path }) => t('validation.maxArray', { field: fieldLabel(path), max }),
      length: ({ length, path }) =>
        t('validation.lengthArray', { field: fieldLabel(path), length }),
    },
  });
}

/**
 * Custom method to allow null or empty string as undefined.
 * Empty string is still considered a value by Yup.
 * So it will still be subject to validation rules unless transformed to `undefined` or `null`.
 * If it is transformed to `null`, the field must be set to nullable() as well.
 *
 * Vuetify clearable text fields set the value to null when cleared
 *
 * The return value is for schema validation/casting purposes only. It does not affect the actual form value.
 * The actual form value is still empty string or null, even tough the validation result is undefined.
 */

yup.addMethod(yup.Schema, 'allowNullOrEmptyString', function () {
  return this.transform((value, originalValue) => {
    if (originalValue === null || originalValue === '') {
      return undefined;
    }
    return value;
  });
});

// Ensure locale is set as early as possible in case schemas are created before plugin install
initializeYupLocale();

export default {
  install(_app) {
    // Initialize after i18n is registered
    // initializeYupLocale();

    // Re-initialize when locale changes (Vue I18n v9, composition API)
    watch(
      () => i18n.global.locale.value,
      () => {
        initializeYupLocale();
      }
    );
  },
};
